import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import ArchiveButton from "@/components/admin/ArchiveButton";
import {
  Bell, Check, CheckCheck, Trash2, FileText, Upload, PenTool,
  Send, Receipt, MessageCircle, Settings, Filter,
  ExternalLink, AlertCircle, Clock, KeyRound, ListTodo, FileSignature, UserCog
} from "lucide-react";

const EVENT_ICONS = {
  document_recu: FileText, document_envoye: Send, depot_portail: Upload,
  piece_manquante_recue: FileText, signature_completee: PenTool,
  transmission_completee: Send, facture_payee: Receipt,
  nouveau_message_portail: MessageCircle, nouvelle_question: MessageCircle,
  reponse_cabinet: MessageCircle,
  // New explicit triggers
  nouveau_document_recu: Upload,
  demande_envoyee: Send,
  signature_demandee: FileSignature,
  facture_envoyee: Receipt,
  tache_assignee: ListTodo,
  password_reset: KeyRound,
  client_info_updated: UserCog,
};
const EVENT_LABELS = {
  document_recu: "Document reçu", document_envoye: "Document envoyé",
  depot_portail: "Dépôt portail", piece_manquante_recue: "Pièce reçue",
  signature_completee: "Signature complétée", transmission_completee: "Transmission",
  facture_payee: "Facture payée", nouveau_message_portail: "Message portail",
  nouvelle_question: "Nouvelle question", reponse_cabinet: "Réponse cabinet",
  // New explicit triggers
  nouveau_document_recu: "Nouveau document reçu",
  demande_envoyee: "Demande envoyée",
  signature_demandee: "Signature demandée",
  facture_envoyee: "Facture envoyée",
  tache_assignee: "Tâche assignée",
  password_reset: "Réinitialisation MDP",
  client_info_updated: "Modification info client",
};

// Categories for filtering
const EVENT_CATEGORIES = {
  action: [
    "piece_manquante_recue", "nouveau_message_portail", "nouvelle_question",
    "nouveau_document_recu",
  ],
  info: [
    "document_recu", "document_envoye", "depot_portail", "reponse_cabinet",
    "demande_envoyee", "signature_demandee", "facture_envoyee",
    "tache_assignee", "password_reset", "client_info_updated",
  ],
  succes: [
    "signature_completee", "transmission_completee", "facture_payee",
  ],
};

function categoryOf(eventType) {
  if (EVENT_CATEGORIES.action.includes(eventType)) return "action";
  if (EVENT_CATEGORIES.succes.includes(eventType)) return "succes";
  return "info";
}

function routeForNotif(n) {
  // Map event type to destination admin screen
  const cid = n.client_id;
  switch (n.event_type) {
    case "document_recu":
    case "document_envoye":
    case "depot_portail":
    case "piece_manquante_recue":
    case "nouveau_document_recu":
      return cid ? `/admin/clients?open=${cid}` : "/admin/clients";
    case "signature_completee":
    case "signature_demandee":
      return cid ? `/admin/clients?open=${cid}` : "/admin/documents/received";
    case "transmission_completee":
      return cid ? `/admin/clients?open=${cid}` : "/admin/clients";
    case "facture_payee":
    case "facture_envoyee":
      return `/admin/comptabilite/transactions-clients${cid ? `?party=${cid}&status=` : ""}`;
    case "demande_envoyee":
      return "/admin/documents/sent";
    case "nouveau_message_portail":
      return "/admin/documents/received";
    case "nouvelle_question":
    case "reponse_cabinet":
      return "/admin/questions";
    case "tache_assignee":
      return "/admin/tasks";
    case "password_reset":
      return "/admin/portail-clients";
    case "client_info_updated":
      return cid ? `/admin/clients?open=${cid}` : "/admin/clients";
    default:
      return cid ? `/admin/clients?open=${cid}` : "/admin/notifications";
  }
}

export default function NotificationsModule() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("inbox");
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  // Filters
  const [fStatus, setFStatus] = useState("all"); // all | unread | read
  const [fCategory, setFCategory] = useState(""); // action | info | succes
  const [fEvent, setFEvent] = useState("");
  const [fPeriod, setFPeriod] = useState(""); // today | 7d | 30d
  const [fSearch, setFSearch] = useState("");

  useEffect(() => { load(); }, []);

  // Auto-refresh every 15s while viewing the inbox
  useEffect(() => {
    if (tab !== "inbox") return undefined;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      const [n, u] = await Promise.all([
        api.get("/notifications", { params: { limit: 200 } }),
        api.get("/notifications/unread-count"),
      ]);
      // Iter87 — backend retourne {count, items}. Fallback array pour retro-compat.
      const data = n.data;
      if (data && Array.isArray(data.items)) {
        setNotifs(data.items);
      } else if (Array.isArray(data)) {
        setNotifs(data);
      } else {
        setNotifs([]);
      }
      setUnread(u.data.count);
    } catch {}
  };

  const markRead = async (id) => {
    try { await api.put(`/notifications/${id}/read`); load(); } catch {}
  };

  const markAllRead = async () => {
    try { await api.put("/notifications/read-all"); toast.success("Tout marque comme lu"); load(); } catch {}
  };

  const deleteNotif = async (id) => {
    try { await api.delete(`/notifications/${id}`); load(); } catch {}
  };

  const timeAgo = (d) => {
    const diff = (Date.now() - new Date(d).getTime()) / 1000;
    if (diff < 60) return "A l'instant";
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
    return new Date(d).toLocaleDateString("fr-CA");
  };

  const filtered = useMemo(() => {
    const now = Date.now();
    const q = fSearch.trim().toLowerCase();
    return notifs.filter(n => {
      if (fStatus === "unread" && n.read) return false;
      if (fStatus === "read" && !n.read) return false;
      if (fCategory && categoryOf(n.event_type) !== fCategory) return false;
      if (fEvent && n.event_type !== fEvent) return false;
      if (fPeriod) {
        const age = now - new Date(n.created_at).getTime();
        const MS_DAY = 86400 * 1000;
        if (fPeriod === "today" && age > MS_DAY) return false;
        if (fPeriod === "7d" && age > 7 * MS_DAY) return false;
        if (fPeriod === "30d" && age > 30 * MS_DAY) return false;
      }
      if (q) {
        const txt = `${n.title || ""} ${n.message || ""} ${n.event_type || ""}`.toLowerCase();
        if (!txt.includes(q)) return false;
      }
      return true;
    });
  }, [notifs, fStatus, fCategory, fEvent, fPeriod, fSearch]);

  const byCategory = useMemo(() => ({
    action: notifs.filter(n => categoryOf(n.event_type) === "action" && !n.read).length,
    info: notifs.filter(n => categoryOf(n.event_type) === "info" && !n.read).length,
    succes: notifs.filter(n => categoryOf(n.event_type) === "succes" && !n.read).length,
  }), [notifs]);

  const handleOpen = (n) => {
    const path = routeForNotif(n);
    if (!n.read) markRead(n.id);
    navigate(path);
  };

  const hasFilter = fStatus !== "all" || fCategory || fEvent || fPeriod || fSearch;

  // Switch to "history" tab → show all + period 30d default (overridable)
  useEffect(() => {
    if (tab === "history") {
      // History uses the same UI as inbox, just clears unread-only by default
      if (fStatus === "unread") setFStatus("all");
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="notifications-module">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-5 h-5" /> Notifications
          </h1>
          {unread > 0 && <p className="text-sm text-blue-600">{unread} non lue(s)</p>}
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={() => setTab("inbox")}
            className={`px-3 py-1.5 rounded-lg text-sm ${tab === "inbox" ? "bg-blue-600 text-white" : "bg-gray-100"}`}
            data-testid="notif-tab-inbox">Boîte</button>
          <button onClick={() => setTab("history")}
            className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${tab === "history" ? "bg-blue-600 text-white" : "bg-gray-100"}`}
            data-testid="notif-tab-history">
            <Clock className="w-4 h-4" /> Historique
          </button>
          <a
            href="/admin/settings?tab=notifications"
            onClick={(e) => { e.preventDefault(); navigate("/admin/settings?tab=notifications"); }}
            className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 hover:bg-gray-200 inline-flex items-center gap-1 text-slate-700"
            title="Configurer les canaux, événements et tests d'envoi"
            data-testid="notif-link-settings">
            <Settings className="w-4 h-4" /> Paramètres
          </a>
        </div>
      </div>

      {(tab === "inbox" || tab === "history") && (
        <>
          {/* Category KPI */}
          <div className="grid grid-cols-3 gap-2 mb-3" data-testid="notif-categories">
            <button onClick={() => setFCategory(fCategory === "action" ? "" : "action")}
              className={`border rounded-xl p-3 text-left transition ${fCategory === "action" ? "ring-2 ring-amber-400 bg-amber-50" : "bg-white hover:bg-gray-50"}`}
              data-testid="notif-cat-action">
              <p className="text-xs text-amber-600 font-semibold flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Action requise
              </p>
              <p className="text-xl font-bold text-gray-900">{byCategory.action}</p>
            </button>
            <button onClick={() => setFCategory(fCategory === "info" ? "" : "info")}
              className={`border rounded-xl p-3 text-left transition ${fCategory === "info" ? "ring-2 ring-blue-400 bg-blue-50" : "bg-white hover:bg-gray-50"}`}
              data-testid="notif-cat-info">
              <p className="text-xs text-blue-600 font-semibold flex items-center gap-1">
                <Bell className="w-3 h-3" /> Informatives
              </p>
              <p className="text-xl font-bold text-gray-900">{byCategory.info}</p>
            </button>
            <button onClick={() => setFCategory(fCategory === "succes" ? "" : "succes")}
              className={`border rounded-xl p-3 text-left transition ${fCategory === "succes" ? "ring-2 ring-emerald-400 bg-emerald-50" : "bg-white hover:bg-gray-50"}`}
              data-testid="notif-cat-succes">
              <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                <Check className="w-3 h-3" /> Succes / Confirmations
              </p>
              <p className="text-xl font-bold text-gray-900">{byCategory.succes}</p>
            </button>
          </div>

          {/* Filters */}
          <div className="bg-white border rounded-xl p-3 mb-3 flex flex-wrap gap-2 items-center" data-testid="notif-filters">
            <Filter className="w-4 h-4 text-gray-400" />
            <input type="search" value={fSearch} onChange={e => setFSearch(e.target.value)}
              placeholder="Recherche..."
              className="border rounded-lg px-2 py-1.5 text-xs w-40"
              data-testid="notif-filter-search" />
            <select value={fStatus} onChange={e => setFStatus(e.target.value)}
              className="border rounded-lg px-2 py-1.5 text-xs" data-testid="notif-filter-status">
              <option value="all">Toutes</option>
              <option value="unread">Non lues</option>
              <option value="read">Lues</option>
            </select>
            <select value={fEvent} onChange={e => setFEvent(e.target.value)}
              className="border rounded-lg px-2 py-1.5 text-xs" data-testid="notif-filter-event">
              <option value="">Tous les types</option>
              {Object.entries(EVENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={fPeriod} onChange={e => setFPeriod(e.target.value)}
              className="border rounded-lg px-2 py-1.5 text-xs" data-testid="notif-filter-period">
              <option value="">Toutes les dates</option>
              <option value="today">Aujourd'hui</option>
              <option value="7d">7 jours</option>
              <option value="30d">30 jours</option>
            </select>
            {hasFilter && (
              <button onClick={() => { setFStatus("all"); setFCategory(""); setFEvent(""); setFPeriod(""); setFSearch(""); }}
                className="text-xs text-indigo-600 hover:underline"
                data-testid="notif-reset-filters">
                Réinitialiser
              </button>
            )}
            {notifs.length > 0 && (
              <button onClick={markAllRead}
                className="text-xs text-blue-600 hover:underline flex items-center gap-1 ml-auto"
                data-testid="mark-all-read-btn">
                <CheckCheck className="w-3.5 h-3.5" /> Tout marquer lu
              </button>
            )}
          </div>

          {/* List */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="divide-y max-h-[65vh] overflow-y-auto">
              {filtered.map(n => {
                const Icon = EVENT_ICONS[n.event_type] || Bell;
                const cat = categoryOf(n.event_type);
                const catColor = cat === "action" ? "text-amber-600 bg-amber-100" : cat === "succes" ? "text-emerald-600 bg-emerald-100" : "text-blue-600 bg-blue-100";
                return (
                  <div key={n.id}
                    className={`px-4 py-3 flex items-start gap-3 ${!n.read ? "bg-blue-50/50" : ""} hover:bg-gray-50 transition cursor-pointer`}
                    data-testid={`notif-${n.id}`}
                    onClick={() => handleOpen(n)}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${!n.read ? catColor : "bg-gray-100 text-gray-400"}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!n.read ? "font-medium" : ""}`}>{n.title}</p>
                      <p className="text-xs text-gray-500 truncate">{n.message}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1.5">
                        <Clock className="w-3 h-3" /> {timeAgo(n.created_at)}
                        <span>·</span>
                        <span className="text-gray-400">{EVENT_LABELS[n.event_type] || n.event_type}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); handleOpen(n); }}
                        className="p-1 text-gray-400 hover:text-blue-600" title="Ouvrir" data-testid={`notif-open-${n.id}`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                      {!n.read && (
                        <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                          className="p-1 text-blue-500 hover:text-blue-700" title="Marquer lu"
                          data-testid={`notif-read-${n.id}`}>
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); deleteNotif(n.id); }}
                        className="p-1 text-gray-400 hover:text-red-500" title="Supprimer"
                        data-testid={`notif-delete-${n.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <span onClick={(e) => e.stopPropagation()}>
                        <ArchiveButton
                          itemType="admin_notification"
                          itemId={n.id}
                          itemLabel="cette notification"
                          onArchived={load}
                          size="xs"
                        />
                      </span>
                    </div>
                  </div>
                );
              })}
              {!filtered.length && (
                <div className="px-4 py-10 text-center text-gray-400" data-testid="notif-empty">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{hasFilter ? "Aucune notification avec ces filtres" : "Aucune notification"}</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
